import * as React from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
} from "@/components/ui/chart";
import { cn } from "@/lib/utils";

export interface ReportLineChartProps {
  data: Array<Record<string, unknown>>;
  dataKeys: Array<{
    key: string;
    label: string;
    color?: string;
    showArea?: boolean;
  }>;
  xAxisKey: string;
  xAxisLabel?: string;
  yAxisLabel?: string;
  height?: number;
  showLegend?: boolean;
  showGrid?: boolean;
  showDots?: boolean;
  curved?: boolean;
  className?: string;
  formatValue?: (value: number) => string;
  formatLabel?: (value: string) => string;
}

export function ReportLineChart({
  data,
  dataKeys,
  xAxisKey,
  xAxisLabel,
  yAxisLabel,
  height = 350,
  showLegend = true,
  showGrid = true,
  showDots = true,
  curved = true,
  className,
  formatValue,
  formatLabel,
}: ReportLineChartProps) {
  const chartConfig = React.useMemo(() => {
    const config: ChartConfig = {};
    dataKeys.forEach((item) => {
      config[item.key] = {
        label: item.label,
        // Use theme object for light/dark mode colors
        theme: {
          light: item.color || "hsl(var(--chart-1))",
          dark: item.color ? item.color.replace(")", " / 0.8)") : "hsl(var(--chart-1) / 0.8)",
        },
      };
    });
    return config;
  }, [dataKeys]);

  return (
    <div className={cn("w-full", className)} style={{ height }}>
      <ChartContainer config={chartConfig}>
        <ComposedChart
          data={data}
          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
          accessibilityLayer
        >
          {showGrid && <CartesianGrid strokeDasharray="3 3" vertical={false} />}
          <XAxis
            dataKey={xAxisKey}
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            label={xAxisLabel ? { value: xAxisLabel, position: "insideBottom", offset: -10 } : undefined}
            tickFormatter={formatLabel}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: "insideLeft" } : undefined}
            tickFormatter={formatValue}
          />
          <ChartTooltip content={<ChartTooltipContent />} />
          {showLegend && <ChartLegend content={<ChartLegendContent />} />}
          {dataKeys.map((item) => (
            <React.Fragment key={item.key}>
              {item.showArea && (
                <Area
                  type={curved ? "monotone" : "linear"}
                  dataKey={item.key}
                  fill={item.color || `var(--color-${item.key})`}
                  fillOpacity={0.2}
                  stroke="none"
                />
              )}
              <Line
                type={curved ? "monotone" : "linear"}
                dataKey={item.key}
                stroke={item.color || `var(--color-${item.key})`}
                strokeWidth={2}
                dot={showDots}
                activeDot={{ r: 6 }}
              />
            </React.Fragment>
          ))}
        </ComposedChart>
      </ChartContainer>
    </div>
  );
}
